import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import styled, { keyframes } from 'styled-components';
import { ToastTheme, toastThemes } from 'utils';

const EFFECT_DURATION = '5s';

const spiderDescent = keyframes`
  0%, 100% {
    height: 0;
  }
  50% {
    height: 60vh;
  }
`;

const spiderSway = keyframes`
  from {
    transform: rotate(-4deg);
  }
  to {
    transform: rotate(4deg);
  }
`;

const Spider = styled.div`
  position: fixed;
  top: 0;
  right: 380px;
  z-index: 9998;
  display: flex;
  align-items: flex-end;
  font-size: 100px;
  line-height: 1;
  background: linear-gradient(#bfb3cc, #bfb3cc) top center / 2px
    calc(100% - 60px) no-repeat;
  transform-origin: top center;
  animation:
    ${spiderDescent} ${EFFECT_DURATION} ease-in-out forwards,
    ${spiderSway} 1.25s ease-in-out infinite alternate;
  pointer-events: none;

  &:before {
    content: '🕷️';
  }
`;

const sleighFlight = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(calc(-100vw - 400px));
  }
`;

const sleighBob = keyframes`
  from {
    transform: translateY(-12px) rotate(-3deg);
  }
  to {
    transform: translateY(12px) rotate(3deg);
  }
`;

const Sleigh = styled.div`
  position: fixed;
  top: 80px;
  right: -400px;
  z-index: 9998;
  font-size: 100px;
  line-height: 1;
  white-space: nowrap;
  animation: ${sleighFlight} ${EFFECT_DURATION} linear forwards;
  pointer-events: none;

  &:before {
    content: '🦌🦌🛷';
    display: inline-block;
    animation: ${sleighBob} 0.8s ease-in-out infinite alternate;
  }
`;

const snowfall = keyframes`
  from {
    transform: translateY(-10vh) rotate(0);
  }
  to {
    transform: translateY(110vh) rotate(360deg);
  }
`;

const Snowflake = styled.div<{ $index: number }>`
  position: fixed;
  top: 0;
  left: ${({ $index }): number => ($index * 37) % 100}vw;
  z-index: 9998;
  font-size: ${({ $index }): number => 20 + ($index % 4) * 8}px;
  animation: ${snowfall} ${({ $index }): number => 3 + ($index % 3) * 0.5}s
    linear ${({ $index }): number => ($index % 5) * 0.25}s both;
  pointer-events: none;

  &:before {
    content: '❄️';
  }
`;

const snowflakes = Array.from({ length: 24 }, (_, index) => index);

const getNewestToastTheme = (): ToastTheme | undefined => {
  const toasts = document.querySelectorAll('.Toastify__toast');
  const newestToast = toasts[toasts.length - 1];
  return toastThemes.find((theme) => newestToast?.classList.contains(theme));
};

const SeasonalToastEffect: FunctionComponent = () => {
  const [effect, setEffect] = useState<{ theme: ToastTheme; key: number }>();
  const toastCount = useRef(0);

  useEffect(
    () =>
      toast.onChange((count) => {
        const theme = getNewestToastTheme();
        if (count > toastCount.current && theme) {
          setEffect((previous) => ({ theme, key: (previous?.key ?? 0) + 1 }));
        }
        toastCount.current = count;
      }),
    [],
  );

  const onEffectEnd = (): void => setEffect(undefined);

  switch (effect?.theme) {
    case 'halloween':
      return <Spider key={effect.key} onAnimationEnd={onEffectEnd} />;
    case 'christmas':
      return (
        <React.Fragment key={effect.key}>
          {snowflakes.map((index) => (
            <Snowflake key={index} $index={index} />
          ))}
          <Sleigh onAnimationEnd={onEffectEnd} />
        </React.Fragment>
      );
    default:
      return null;
  }
};

export default SeasonalToastEffect;
